import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import { useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import type { ComponentProps } from 'react'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import LoyaltyTierBenefitMessageBanner from './LoyaltyTierBenefitMessageBanner'
import getIsLoyaltyLeadRegion from 'loyalty/selectors/getIsLoyaltyLeadRegion'
import FormatCurrency from 'components/Common/FormatCurrency'

type InheritedLoyaltyTierBenefitMessageBannerProps = Pick<ComponentProps<typeof LoyaltyTierBenefitMessageBanner>, 'flat' | 'icon'>

interface Props extends InheritedLoyaltyTierBenefitMessageBannerProps {
  benefitType: App.LoyaltyBenefitType
  /**
   * @default benefit's availability
   */
  benefitAvailability?: number
}

function LoyaltyTierBenefitAvailabilityMessageBanner({
  benefitType,
  benefitAvailability,
  ...rest
}: Props) {
  const intl = useIntl()
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const statusReviewDate = useAppSelector(state => state.loyalty.account.data?.statusReviewDate)
  const benefitView = useLoyaltyUserBenefitView(benefitType)
  const isLeadLoyaltyRegion = useAppSelector(getIsLoyaltyLeadRegion)

  if (!benefitView) return null

  const availability = benefitAvailability ?? benefitView.availability

  if (!availability) return null

  const availableToRedeemText = intl.formatMessage({
    id: '0EHp0YyUyC',
    defaultMessage: 'Available to redeem',
    description: 'Prefix for loyalty benefit availability message',
  })
  const unlimitedText = intl.formatMessage({
    id: 'SHLtwKkxzE',
    defaultMessage: 'unlimited',
    description: 'Label when benefit availability is unlimited',
  })
  const voucherPluralText = intl.formatMessage(
    {
      id: 'QX51HceS8z',
      defaultMessage: '{count, plural, one {voucher} other {vouchers}}',
      description: 'Pluralized voucher noun for loyalty benefit (count is displayed separately)',
    },
    { count: availability },
  )
  const percentOffText = intl.formatMessage(
    {
      id: 'NMV2q/4RQJ',
      defaultMessage: '{amount}% off',
      description: 'Discount percentage for discounted insurance benefit',
    },
    { amount: benefitView.benefit.value?.amount ?? 0 },
  )

  return <LoyaltyTierBenefitMessageBanner
    tier={displayTier}
    benefitType={benefitType}
    density="compact"
    endContent={!!statusReviewDate && <div>
      <FormattedMessage
        id="RmXB7STXZa"
        defaultMessage="Expires <bold>{date}</bold>"
        description="Expiry date label for loyalty benefit availability"
        values={{
          date: <FormatDateTime value={statusReviewDate} month="short" year="numeric" />,
          bold: (chunks) => <b>{chunks}</b>,
        }}
      />
    </div>}
    {...rest}
  >
    {availableToRedeemText}{' '}
    {benefitType !== 'discounted_insurance' && <b>
      {availability === Infinity && unlimitedText}
      {availability !== Infinity && <>x<FormatDecimal value={availability} /></>}
      {(benefitType === 'travelshoot_credit' || benefitType === 'tour_exp_credit') && !!benefitView.benefit.value?.amount && <>
        {isLeadLoyaltyRegion && <>{' '}<FormatCurrency value={benefitView.benefit.value?.amount ?? 0} format="dynamic" /> {voucherPluralText}</>}
        {!isLeadLoyaltyRegion && <>{' '}{voucherPluralText}</>}
      </>}
    </b>}
    {benefitType === 'discounted_insurance' && <b>{percentOffText}</b>}
  </LoyaltyTierBenefitMessageBanner>
}

export default LoyaltyTierBenefitAvailabilityMessageBanner
